import {
  CreateCargoClaimCommandInput,
  SiteDetailsInput,
  UpdateCargoClaimAttachmentsCommandInput,
  UpdateCargoClaimCommandInput,
} from "@amzn/ttechclaimintakeservice-client";
import { User } from "src/auth/types/interfaces";
import { buildAttachments } from "src/forms/FileUpload/FileUploadUtilities";
import { S3StagedFile } from "src/forms/FileUpload/types";
import { formatCurrencyStringToNumber } from "src/app/Cargo/utils";
import carriersEnum from "src/jsonForms/enums/cargo/carriers.json";
import claimTypesEnum from "src/jsonForms/enums/cargo/claimTypes.json";

/**
 * Lookup map from claimTypeId enum values to their display labels.
 * Built dynamically from claimTypes.json to stay in sync with the enum file.
 */
const CLAIM_TYPE_ID_TO_DESCRIPTION: Record<string, string> = Object.fromEntries(
  claimTypesEnum.map((c) => [c.value, c.label])
);

/** Resolve carrier display name from carrierId using the static enum */
const CARRIER_ID_TO_LABEL: Record<string, string> = Object.fromEntries(
  carriersEnum.map((c) => [c.value, c.label])
);

interface CreateCargoClaimMeta {
  user: User;
  amazonClaimReferenceId: string;
}

interface UpdateCargoClaimMeta {
  user: User;
  amazonClaimReferenceId: string;
  claimNumber: string;
  incidentDate: string;
}

/**
 * Flattens nested JSON Forms data produced by grouped layout objects
 * (e.g. carrierDetails.carrierId) into a flat key-value map.
 * Top-level primitives/arrays are kept as-is; nested objects have their
 * children promoted to the top level.
 */
function flattenData(data: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      // Nested group object - promote children
      for (const [childKey, childValue] of Object.entries(
        value as Record<string, unknown>
      )) {
        if (childKey in flat) {
          throw new Error(
            `flattenData: key collision for "${childKey}" — nested value would overwrite existing`
          );
        }
        flat[childKey] = childValue;
      }
    } else {
      if (key in flat) {
        throw new Error(
          `flattenData: key collision for "${key}" — top-level value would overwrite existing`
        );
      }
      flat[key] = value;
    }
  }
  return flat;
}

/**
 * Helper to read a string value from flat JSON Forms data.
 */
const str = (data: Record<string, unknown>, key: string): string =>
  (data[key] as string) ?? "";

/**
 * Helper to read an optional string value (returns undefined if absent).
 */
const optStr = (
  data: Record<string, unknown>,
  key: string
): string | undefined => (data[key] as string) || undefined;

/**
 * Builds the CreateCargoClaimCommandInput payload from JSON Forms data (Stage 1).
 * Flattens nested layout groups before mapping field keys to the API command input.
 */
export function buildCreateCargoClaimPayload(
  data: Record<string, unknown>,
  meta: CreateCargoClaimMeta
): CreateCargoClaimCommandInput {
  const d = flattenData(data);

  const companyAddress: SiteDetailsInput = {
    addressLine1: optStr(d, "companyAddressLine1"),
    addressLine2: optStr(d, "companyAddressLine2"),
    city: optStr(d, "companyAddressCity"),
    state: optStr(d, "companyAddressState"),
    zipCode: optStr(d, "companyAddressZipCode"),
  };

  return {
    amazonClaimReferenceId: meta.amazonClaimReferenceId,

    // Claim reporter information from user
    claimReporterFirstName: meta.user?.givenName || "",
    claimReporterLastName: meta.user?.familyName || "",
    claimReporterAlias: meta.user?.id || "",
    claimReporterJobDescription: meta.user?.jobDescription || "",
    claimReporterEmailAddress: meta.user?.email || "",

    // Business Details
    businessUnit: optStr(d, "businessUnit"),
    vertical: optStr(d, "vertical"),
    companyName: optStr(d, "companyName"),
    pointOfContactName: optStr(d, "pointOfContactName"),
    pointOfContactEmailAddress: optStr(d, "pointOfContactEmailAddress"),
    additionalContactEmailAddresses: [
      str(d, "additionalContactEmailAddress0"),
      str(d, "additionalContactEmailAddress1"),
    ].filter(Boolean),
    companyAddress,
    countryId: optStr(d, "countryId"),

    // Shipment Details
    carrierId: optStr(d, "carrierId"),
    // For "Other - Type Carrier name" (31) use the user-entered name.
    // For all other carriers resolve the display label from the enum.
    carrierName:
      str(d, "carrierId") === "31"
        ? optStr(d, "carrierName")
        : CARRIER_ID_TO_LABEL[str(d, "carrierId")] || undefined,
    // standardCarrierAlphaCode is only relevant when carrierId is "30" (Other - SCAC)
    standardCarrierAlphaCode:
      str(d, "carrierId") === "30"
        ? optStr(d, "standardCarrierAlphaCode")
        : undefined,
    carrierEmailAddress: optStr(d, "carrierEmailAddress"),
    carrierType: optStr(d, "carrierType"),
    transportationMode: optStr(d, "transportationMode"),
    trailerType: optStr(d, "trailerType"),
    cmrReserved: optStr(d, "cmrReserved"),
    pncTimelineMet: optStr(d, "pncTimelineMet"),
    vridLoadId: optStr(d, "vridLoadId"),
    amazonOrderNumber: optStr(d, "amazonOrderNumber"),
    modeOfTransport: optStr(d, "modeOfTransport"),
    flightNumber: optStr(d, "flightNumber"),
    vesselName: optStr(d, "vesselName"),
    containerNumber: optStr(d, "containerNumber"),
    modeOfTransportOtherDescription: optStr(
      d,
      "modeOfTransportOtherDescription"
    ),
    shipmentType: optStr(d, "shipmentType"),
    billOfLadingNumber: optStr(d, "billOfLadingNumber"),
    billOfLadingDate: optStr(d, "billOfLadingDate"),
    laneOrigination: optStr(d, "laneOrigination"),
    billOfLadingOriginationCity: optStr(d, "billOfLadingOriginationCity"),
    billOfLadingOriginationCountry: optStr(d, "billOfLadingOriginationCountry"),
    laneDestination: optStr(d, "laneDestination"),
    billOfLadingDestinationCity: optStr(d, "billOfLadingDestinationCity"),
    billOfLadingDestinationCountry: optStr(d, "billOfLadingDestinationCountry"),
    cargoDescription: optStr(d, "cargoDescription"),
    sealStatus: optStr(d, "sealStatus"),
    invoiceNumber: optStr(d, "invoiceNumber"),
    invoiceCurrencyCode: optStr(d, "invoiceCurrencyCode"),
    totalInvoiceValue: optStr(d, "totalInvoiceValue")
      ? formatCurrencyStringToNumber(str(d, "totalInvoiceValue"))
      : undefined,

    // Incident Details
    incidentDate: optStr(d, "incidentDate"),
    typeOfClaim: optStr(d, "typeOfClaim"),
    claimTypeId: optStr(d, "claimTypeId"),
    claimTypeDescription:
      CLAIM_TYPE_ID_TO_DESCRIPTION[str(d, "claimTypeId")] || undefined,
    totalClaimCurrencyCode: optStr(d, "totalClaimCurrencyCode"),
    totalClaimValue: optStr(d, "totalClaimValue")
      ? formatCurrencyStringToNumber(str(d, "totalClaimValue"))
      : undefined,
    expectedArrivalDate: optStr(d, "expectedArrivalDate"),
    locationOfIncident: optStr(d, "locationOfIncident"),
    detailedIncidentSummary: optStr(d, "detailedIncidentSummary"),
    policeReportAvailable: optStr(d, "policeReportAvailable"),
    cargoOrPropertyLocation: optStr(d, "cargoOrPropertyLocation"),
    contactName: optStr(d, "contactName"),
    contactPhoneNumber: optStr(d, "contactPhoneNumber"),
    contactEmailAddress: optStr(d, "contactEmailAddress"),
    additionalInformation: optStr(d, "additionalInformation"),
  };
}

/**
 * Builds the UpdateCargoClaimCommandInput payload from JSON Forms data (Stage 2).
 * Flattens nested layout groups (e.g. singleItemDetailsGroup) before mapping
 * field keys to the API command input.
 */
export function buildUpdateCargoClaimPayload(
  data: Record<string, unknown>,
  meta: UpdateCargoClaimMeta
): UpdateCargoClaimCommandInput {
  const d = flattenData(data);

  return {
    amazonClaimReferenceId: meta.amazonClaimReferenceId,
    claimReporterFirstName: meta.user?.givenName || "",
    claimReporterLastName: meta.user?.familyName || "",
    claimReporterAlias: meta.user?.id || "",
    claimReporterJobDescription: meta.user?.jobDescription || "",
    claimNumber: meta.claimNumber,
    incidentDate: meta.incidentDate,

    // Claim Details
    quantity: optStr(d, "quantity"),
    handlingUnitIdentifier: optStr(d, "handlingUnitIdentifier"),
    weight: optStr(d, "weight"),
    weightUnitType: optStr(d, "weightUnitType"),
    descriptionOfCargoOrProperty: optStr(d, "descriptionOfCargoOrProperty"),
    materialPartNumber: optStr(d, "materialPartNumber"),
    amazonPartNumber: optStr(d, "amazonPartNumber"),
    purchaseOrderNumber: optStr(d, "purchaseOrderNumber"),
    warehouseManagementSystemOrderNumber: optStr(
      d,
      "warehouseManagementSystemOrderNumber"
    ),
    amazonStandardIdentificationNumber: optStr(
      d,
      "amazonStandardIdentificationNumber"
    ),
    multipleItemClaimAttachment: d.multipleItemClaimAttachment
      ? buildAttachments(d.multipleItemClaimAttachment as S3StagedFile[])
      : [],
  };
}

/**
 * Builds the UpdateCargoClaimAttachmentsCommandInput payload from JSON Forms data (Stage 3).
 * Stage 3 has no nested groups so flattenData is a no-op, but we call it
 * for consistency and future-proofing.
 */
export function buildUpdateCargoClaimAttachmentsPayload(
  data: Record<string, unknown>,
  meta: UpdateCargoClaimMeta
): UpdateCargoClaimAttachmentsCommandInput {
  const d = flattenData(data);

  return {
    amazonClaimReferenceId: meta.amazonClaimReferenceId,
    claimReporterFirstName: meta.user?.givenName || "",
    claimReporterLastName: meta.user?.familyName || "",
    claimReporterAlias: meta.user?.id || "",
    claimReporterJobDescription: meta.user?.jobDescription || "",
    claimNumber: meta.claimNumber,
    incidentDate: meta.incidentDate,

    // File Attachments
    commercialInvoice: buildAttachments(
      (d.commercialInvoice as S3StagedFile[]) || []
    ),
    billOfLadingFront: buildAttachments(
      (d.billOfLadingFront as S3StagedFile[]) || []
    ),
    billOfLadingReverse: buildAttachments(
      (d.billOfLadingReverse as S3StagedFile[]) || []
    ),
    damagedPackagingPhotos: buildAttachments(
      (d.damagedPackagingPhotos as S3StagedFile[]) || []
    ),
    damagedContentsPhotos: buildAttachments(
      (d.damagedContentsPhotos as S3StagedFile[]) || []
    ),
    proofOfDelivery: buildAttachments(
      (d.proofOfDelivery as S3StagedFile[]) || []
    ),
    packingSlip: buildAttachments((d.packingSlip as S3StagedFile[]) || []),
    proofFromCarrier: buildAttachments(
      (d.proofFromCarrier as S3StagedFile[]) || []
    ),
    policeReport: buildAttachments((d.policeReport as S3StagedFile[]) || []),
    additionalDocuments: buildAttachments(
      (d.additionalDocuments as S3StagedFile[]) || []
    ),
  };
}
